/**
 * Preview-match model: what the server suggests for each line of the draft (reuse an existing line or create a new one), the
 * engineer's choices, and what is sent with Save. Pure functions, no React. The response shape is the preview-match contract
 * (POST /api/learning/review-items/preview-match); every field may be absent and reads as "new".
 */
export const MATCH_STATUS = Object.freeze({ IDLE: "idle", LOADING: "loading", READY: "ready", ERROR: "error" });
export const DECISION = Object.freeze({ EXISTING: "existing", NEW: "new" });
export const MATCH_SECTION = Object.freeze({ SYMPTOMS: "symptoms", STEPS: "steps", CAUSE: "cause", FIX: "fix" });
// The server's match budget is 8 s and it answers a degraded 200 when it runs out; wait 2 s longer so that answer arrives.
export const MATCH_TIMEOUT_MS = 10000;
// A candidate at or above HIGH_SCORE reads as a match; below it (down to the server's floor) reads as "check this match".
export const HIGH_SCORE = 0.75;
export const MAX_CANDIDATES = 5;

/** How one row reads: reused (clear match, or confirmed by the engineer), check (weak match, not confirmed yet) or new. */
export const ROW_STATE = Object.freeze({ REUSED: "reused", CHECK: "check", NEW: "new" });

// The save body names the steps list "resolution_steps"; the preview response names it "steps".
const LISTS = Object.freeze([MATCH_SECTION.SYMPTOMS, MATCH_SECTION.STEPS]);
const SINGLES = Object.freeze([MATCH_SECTION.CAUSE, MATCH_SECTION.FIX]);
const SAVE_KEY = Object.freeze({ [MATCH_SECTION.SYMPTOMS]: "symptoms", [MATCH_SECTION.STEPS]: "resolution_steps" });
const NEW_CHOICE = Object.freeze({ decision: DECISION.NEW, selected_id: null, confirmed: false });
const isSingle = (section) => SINGLES.includes(section);

/** Two drafts with the same key need the same answer: category and every matched line. */
export const matchKey = (category, draft) =>
  JSON.stringify([category ? category.key : null, draft.symptoms, draft.steps, draft.rootCause, draft.fixText]);

export const idleMatch = () => ({ status: MATCH_STATUS.IDLE, key: null, data: null, choices: null });
export const loadingMatch = (key) => ({ status: MATCH_STATUS.LOADING, key, data: null, choices: null });

// `confirmed` is the engineer's own pick (a weak server preselection stays "check" until then); never sent to the server.
const choiceOf = (row) => (row && row.decision === DECISION.EXISTING && row.selected_id
  ? { decision: DECISION.EXISTING, selected_id: row.selected_id, confirmed: false } : NEW_CHOICE);
const rowsOf = (data, section) => (data && Array.isArray(data[section]) ? data[section] : []);
const singleOf = (data, section) => (data && data[section] ? data[section] : null);

/** The server's own preselection for every row, as the engineer's starting choices. */
export function resolvedMatch(key, data) {
  const choices = {};
  SINGLES.forEach((section) => { choices[section] = choiceOf(singleOf(data, section)); });
  LISTS.forEach((section) => {
    choices[section] = {};
    rowsOf(data, section).forEach((row) => { choices[section][row.index] = choiceOf(row); });
  });
  return { status: MATCH_STATUS.READY, key, data, choices };
}

export const pickedMatch = (match, { section, index, decision, selected_id: selectedId }) => {
  const choice = decision === DECISION.EXISTING && selectedId ? { decision, selected_id: selectedId, confirmed: true } : NEW_CHOICE;
  const choices = isSingle(section)
    ? { ...match.choices, [section]: choice }
    : { ...match.choices, [section]: { ...match.choices[section], [index]: choice } };
  return { ...match, choices };
};

/** The row the server returned for a line (null while loading, on error, or when the server left it out). */
export function rowAt(match, section, index) {
  if (!match.data) return null;
  if (isSingle(section)) return singleOf(match.data, section);
  return rowsOf(match.data, section).find((row) => row.index === index) || null;
}

export function choiceAt(match, section, index) {
  if (!match.choices) return NEW_CHOICE;
  return (isSingle(section) ? match.choices[section] : match.choices[section][index]) || NEW_CHOICE;
}

/** The candidate the choice points at, or null when the line is new (or the id is unknown). */
export function chosenAt(match, section, index) {
  const row = rowAt(match, section, index);
  const choice = choiceAt(match, section, index);
  if (!row || choice.decision !== DECISION.EXISTING) return null;
  return (row.candidates || []).find((c) => c.id === choice.selected_id) || null;
}

export const isWeak = (candidate) => !!candidate && typeof candidate.score === "number" && candidate.score < HIGH_SCORE;

/** reused / check / new for one line: a weak server preselection reads as "check" until the engineer picks it. */
export function rowStateAt(match, section, index) {
  const chosen = chosenAt(match, section, index);
  if (!chosen) return ROW_STATE.NEW;
  return isWeak(chosen) && !choiceAt(match, section, index).confirmed ? ROW_STATE.CHECK : ROW_STATE.REUSED;
}

const everyLine = (match) => {
  const all = SINGLES.map((section) => [section, 0]);
  LISTS.forEach((section) => rowsOf(match.data, section).forEach((row) => all.push([section, row.index])));
  return all;
};

/** {reused, check, new, pending} over every matched line (symptoms, steps, root cause, fix); null when there is nothing to count. */
export function matchSummary(match, draft) {
  if (match.status === MATCH_STATUS.LOADING) {
    return { reused: 0, check: 0, new: 0, pending: draft.symptoms.length + draft.steps.length + SINGLES.length };
  }
  if (match.status !== MATCH_STATUS.READY) return null;
  const states = everyLine(match).map(([section, index]) => rowStateAt(match, section, index));
  const count = (state) => states.filter((s) => s === state).length;
  return { reused: count(ROW_STATE.REUSED), check: count(ROW_STATE.CHECK), new: count(ROW_STATE.NEW), pending: 0 };
}

/** The pattern verdict: {existing, label} once the rows are resolved, else null. */
export function patternVerdict(match) {
  if (match.status !== MATCH_STATUS.READY) return null;
  const pattern = (match.data && match.data.pattern) || {};
  return { existing: pattern.decision === DECISION.EXISTING && !!pattern.selected_id, label: pattern.label || "" };
}

const issuesOf = (match) => (match.data && Array.isArray(match.data.issues) ? match.data.issues : []);
export const issuesAt = (match, section, index) =>
  issuesOf(match).filter((issue) => issue.section === section && (isSingle(section) || issue.index === index));
// Category issues (category_not_found warns, category_invalid blocks) belong to no line: the server sends them with section "category".
export const CATEGORY_SECTION = "category";
export const categoryIssues = (match) => issuesOf(match).filter((issue) => issue.section === CATEGORY_SECTION);
export const blockingIssues = (match) => (match.status === MATCH_STATUS.READY ? issuesOf(match).filter((i) => i.blocking) : []);

const sent = ({ decision, selected_id: selectedId }) => ({ decision, selected_id: selectedId });

/** The `match` object sent with Save (null when nothing was matched, so the save stays as before). */
export function matchPayload(match) {
  if (match.status !== MATCH_STATUS.READY) return null;
  const pattern = (match.data && match.data.pattern) || {};
  const linesOf = (section) => rowsOf(match.data, section).map((row) => ({ index: row.index, ...sent(choiceAt(match, section, row.index)) }));
  const singleChoice = (section) => (singleOf(match.data, section) ? sent(choiceAt(match, section, 0)) : null);
  return {
    pattern: { decision: pattern.decision === DECISION.EXISTING ? DECISION.EXISTING : DECISION.NEW, selected_id: pattern.selected_id || null },
    [SAVE_KEY.symptoms]: linesOf(MATCH_SECTION.SYMPTOMS),
    [SAVE_KEY.steps]: linesOf(MATCH_SECTION.STEPS),
    cause: singleChoice(MATCH_SECTION.CAUSE),
    fix: singleChoice(MATCH_SECTION.FIX),
  };
}

const metaFor = (lines, meta) => lines.map((text) => (meta && meta[text]) || null);

/**
 * `symptom_meta` / `step_meta` for a cleaned draft: arrays the same length as its symptoms and steps, holding where each line came
 * from (looked up by its exact text) or null for a line the engineer wrote or edited. Empty object when no line has any, so the
 * request and the save stay as they were.
 */
export function metaFields(draft) {
  const symptomMeta = metaFor(draft.symptoms, draft.lineMeta);
  const stepMeta = metaFor(draft.steps, draft.stepMeta);
  if (!symptomMeta.some(Boolean) && !stepMeta.some(Boolean)) return {};
  return { symptom_meta: symptomMeta, step_meta: stepMeta };
}

/** The preview-match request body (without session_id, added by the caller). The fix is sent so the server can match it too. */
export const matchRequest = (category, draft) => ({
  category: category ? category.key : null,
  symptoms: draft.symptoms,
  resolution_steps: draft.steps,
  root_cause: draft.rootCause,
  fix_kind: draft.fixKind || null,
  fix_text: draft.fixText,
  ...metaFields(draft),
});
